import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { AuthCard, AuthError, authButtonCls, authInputCls, authLabelCls } from "@/components/AuthCard";
import { resendSignupCode, verifySignupCode } from "@/lib/auth";

/** /signup/verify: the 6-digit code from the email. The email field is
 * there too, so the code can be entered from any device. */
export function SignupVerify() {
  const location = useLocation();
  const navigate = useNavigate();
  const sentTo = (location.state as { email?: string } | null)?.email ?? "";
  const [email, setEmail] = useState(sentTo);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = email.trim() !== "" && /^\d{6}$/.test(code.trim()) && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      const { signup_token } = await verifySignupCode(email.trim(), code.trim());
      navigate("/signup/finish", { state: { signupToken: signup_token } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn't work");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!email.trim()) return;
    setError("");
    setNotice("");
    try {
      await resendSignupCode(email.trim());
      setNotice("A new code is on its way.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send a new code");
    }
  }

  return (
    <AuthCard
      title="Check your email"
      subtitle={sentTo ? `We sent a 6-digit code to ${sentTo}.` : "Enter the 6-digit code we emailed you."}
      onSubmit={handleSubmit}
    >
      <AuthError message={error} />
      {notice && (
        <div role="status" className="rounded-md border border-safe-green/30 bg-safe-green/10 px-3 py-2 text-sm text-safe-green">
          {notice}
        </div>
      )}
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className={authLabelCls}>Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className={authInputCls}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="code" className={authLabelCls}>6-digit code</label>
          <input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            className={`${authInputCls} font-mono tracking-[0.4em]`}
          />
        </div>
      </div>
      <button type="submit" disabled={!canSubmit} className={authButtonCls}>
        {submitting ? "Checking…" : "Verify"}
      </button>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={handleResend} className="text-fg-muted hover:text-electric-blue">
          Send a new code
        </button>
        <Link to="/signup" className="text-fg-muted hover:text-electric-blue">
          Start again
        </Link>
      </div>
    </AuthCard>
  );
}
